import { RESERVOIR_CATALOG, RESERVOIR_SEQUENCE, listReservoirs } from '@/services/reservoirCatalog.js'

const HYDRO_API_URL = (process.env.VUE_APP_HYDRO_API_URL || '').replace(/\/$/, '')
const REQUIRE_API = process.env.VUE_APP_HYDRO_REQUIRE_API === 'true'

const FALLBACK_REGIONS = [
  { region_id: 'irrigation-east', name: '东干渠灌区', actual_supply_10k_m3: 171.5, demand_10k_m3: 194.2, warning_level: 'yellow' },
  { region_id: 'urban-guxian', name: '古贤城镇供水区', actual_supply_10k_m3: 98.4, demand_10k_m3: 101.0, warning_level: 'blue' },
  { region_id: 'industry-south', name: '南部工业园区', actual_supply_10k_m3: 66.8, demand_10k_m3: 79.3, warning_level: 'yellow' },
  { region_id: 'ecology-river', name: '河道生态基流', actual_supply_10k_m3: 60.2, demand_10k_m3: 58.0, warning_level: 'normal' },
]

const FALLBACK_STATION_SERIES = {
  station_id: 'guxian-reservoir',
  name: '古贤水库',
  data_source: 'frontend_fallback',
  interval_minutes: 60,
  points: 24,
  series: Array.from({ length: 24 }, (_, index) => {
    const wave = Math.sin(index / 23 * Math.PI)
    return {
      timestamp: `2026-07-04T${String(index).padStart(2, '0')}:00:00+08:00`,
      water_level_m: Math.round((616.42 + wave * 0.42) * 100) / 100,
      inflow_m3s: Math.round((1268.5 * (0.82 + 0.34 * wave)) * 10) / 10,
      outflow_m3s: Math.round((942.0 * (0.9 + 0.18 * wave)) * 10) / 10,
      rainfall_mm: Math.round((12.4 * Math.max(0.18, 1 - index / 24)) * 10) / 10,
      sediment_kg_m3: Math.round((18.6 * (0.74 + 0.4 * wave)) * 100) / 100,
    }
  }),
}

/* 从水库目录动态生成站点别名，保证所有水库都能被监测查询匹配到 */
const RESERVOIR_STATION_ALIASES = listReservoirs().map(r => ({
  station_id: r.id,
  aliases: [r.name, ...r.aliases],
  name: r.name,
  location: r.location,
  reservoir: r,
}))

const STATION_ALIASES = [
  ...RESERVOIR_STATION_ALIASES,
  { station_id: 'longmen-rain', aliases: ['龙门', '龙门雨量', '雨量站', 'longmen'], name: '龙门雨量站', location: [110.5562, 35.6153] },
  { station_id: 'hecheng-section', aliases: ['合成', '合成断面', '水文站', 'hecheng'], name: '合成断面水文站', location: [110.45, 35.60] },
]

const METRIC_ALIASES = [
  { key: 'water_level_m', aliases: ['水位', '库水位', '水深'], label: '水位', unit: 'm', panel: 'monitoring' },
  { key: 'inflow_m3s', aliases: ['入库', '来水', '入流', '入库流量', '流量'], label: '入库流量', unit: 'm3/s', panel: 'monitoring' },
  { key: 'outflow_m3s', aliases: ['出库', '下泄', '出流', '出库流量'], label: '出库流量', unit: 'm3/s', panel: 'monitoring' },
  { key: 'rainfall_mm', aliases: ['降雨', '雨量', '降水'], label: '雨量', unit: 'mm', panel: 'monitoring' },
  { key: 'sediment_kg_m3', aliases: ['含沙', '含沙量', '泥沙'], label: '含沙量', unit: 'kg/m3', panel: 'monitoring' },
  { key: 'water_quality', aliases: ['水质', '浊度', 'ph', '溶解氧'], label: '水质', unit: '', panel: 'monitoring' },
  { key: 'device_status', aliases: ['设备', '在线', '离线', '故障'], label: '设备状态', unit: '', panel: 'monitoring' },
  { key: 'supplyDemand', aliases: ['供需', '缺口', '供水', '需水', '平衡'], label: '供需平衡', unit: '万m3', panel: 'supplyDemand' },
]

async function getHydro(path, fallback) {
  if (HYDRO_API_URL === 'local') {
    if (REQUIRE_API) throw new Error(`${path} requires API but VUE_APP_HYDRO_API_URL=local`)
    return fallback()
  }
  try {
    const response = await fetch(`${HYDRO_API_URL}${path}`)
    if (!response.ok) throw new Error(`${path} responded ${response.status}`)
    const payload = await response.json()
    return { ...payload, source: payload.data_source || 'api' }
  } catch (error) {
    if (REQUIRE_API) throw error
    console.warn(`[businessDataService] ${path} unavailable, using fallback data:`, error)
    return { ...fallback(), source: 'frontend_fallback' }
  }
}

export function fetchStations() {
  return getHydro('/hydro/stations', () => ({
    count: 1,
    data_source: 'frontend_fallback',
    stations: [{ station_id: 'guxian-reservoir', name: '古贤水库', type: 'reservoir', status: 'warning' }],
  }))
}

export function fetchStationSnapshot(stationId = 'guxian-reservoir') {
  // 优先从水库目录获取快照数据
  const reservoir = RESERVOIR_CATALOG[stationId]
  if (reservoir && reservoir.snapshot) {
    const snap = reservoir.snapshot
    const now = new Date()
    const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:00:00+08:00`
    return Promise.resolve({
      station_id: stationId,
      name: reservoir.name,
      data_source: 'reservoir_catalog',
      timestamp: ts,
      water_level_m: snap.level,
      inflow_m3s: snap.inflow,
      outflow_m3s: snap.outflow,
      rainfall_mm: Math.round(Math.random() * 15 * 10) / 10,
      sediment_kg_m3: reservoir.river === '黄河干流' ? 18.6 : Math.round(Math.random() * 5 * 100) / 100,
      status: snap.level >= (reservoir.characteristic_levels || {}).design_flood_level ? 'warning' : 'normal',
      device_status: { online: 18, offline: 0, warning: 0 },
      location: reservoir.location,
      source: 'reservoir_catalog',
    })
  }
  // 降级到 API 或默认兜底
  return getHydro(`/hydro/stations/${stationId}/snapshot`, () => ({
    station_id: stationId,
    name: '古贤水库',
    data_source: 'frontend_fallback',
    timestamp: '2026-07-04T08:00:00+08:00',
    water_level_m: 616.42,
    inflow_m3s: 1268.5,
    outflow_m3s: 942.0,
    rainfall_mm: 12.4,
    sediment_kg_m3: 18.6,
    status: 'warning',
    device_status: { online: 18, offline: 1, warning: 2 },
  }))
}

export function fetchStationSeries(stationId = 'guxian-reservoir', points = 24) {
  return getHydro(`/hydro/stations/${stationId}/series?points=${points}`, () => ({
    ...FALLBACK_STATION_SERIES,
    station_id: stationId,
    points,
    series: FALLBACK_STATION_SERIES.series.slice(0, points),
  }))
}

export function fetchSupplyDemandSummary() {
  return getHydro('/hydro/supply-demand/summary', () => {
    const totalDemand = FALLBACK_REGIONS.reduce((sum, item) => sum + item.demand_10k_m3, 0)
    const totalActual = FALLBACK_REGIONS.reduce((sum, item) => sum + item.actual_supply_10k_m3, 0)
    const totalDeficit = FALLBACK_REGIONS.reduce((sum, item) => sum + Math.max(0, item.demand_10k_m3 - item.actual_supply_10k_m3), 0)
    return {
      data_source: 'frontend_fallback',
      timestamp: '2026-07-04T08:00:00+08:00',
      total_demand_10k_m3: Math.round(totalDemand * 10) / 10,
      total_actual_supply_10k_m3: Math.round(totalActual * 10) / 10,
      total_deficit_10k_m3: Math.round(totalDeficit * 10) / 10,
      warning_region_count: 2,
      regions: FALLBACK_REGIONS,
    }
  })
}

export function fetchSupplyDemandRegions() {
  return getHydro('/hydro/supply-demand/regions', () => ({
    data_source: 'frontend_fallback',
    timestamp: '2026-07-04T08:00:00+08:00',
    regions: FALLBACK_REGIONS,
  }))
}

export function toSupplyDemandRows(regions = []) {
  return regions.map(region => ({
    name: region.name,
    supply: Math.round(Number(region.actual_supply_10k_m3 || 0) * 10000),
    demand: Math.round(Number(region.demand_10k_m3 || 0) * 10000),
    warningLevel: region.warning_level || 'normal',
  }))
}

export function toMonitoringChartData(payload) {
  const rows = payload && Array.isArray(payload.series) ? payload.series : []
  return {
    times: rows.map(row => {
      const date = new Date(row.timestamp)
      return Number.isNaN(date.getTime()) ? String(row.timestamp).slice(11, 16) : `${String(date.getHours()).padStart(2, '0')}:00`
    }),
    inflow: rows.map(row => Math.round(Number(row.inflow_m3s || 0))),
    outflow: rows.map(row => Math.round(Number(row.outflow_m3s || 0))),
    reservoirLevel: rows.map(row => row.water_level_m == null ? null : Number(row.water_level_m)),
  }
}

export function parseBusinessDataQuestion(text) {
  const raw = String(text || '')
  const asksData = /数据|多少|情况|汇报|监测|实时|当前|现在|查询|看一下|怎么样|状态|缺口|预警/.test(raw)
  const station = STATION_ALIASES.find(item => item.aliases.some(alias => raw.toLowerCase().includes(alias.toLowerCase()))) || STATION_ALIASES[0]
  const metric = METRIC_ALIASES.find(item => item.aliases.some(alias => raw.toLowerCase().includes(alias.toLowerCase())))
  if (!metric && !asksData) return null
  if (!metric && !/(水位|流量|雨量|含沙|水质|设备|供需|供水|需水|缺口)/.test(raw)) return null
  return { station, metric: metric || METRIC_ALIASES[0], text: raw }
}

export async function resolveBusinessDataQuestion(text) {
  const parsed = parseBusinessDataQuestion(text)
  if (!parsed) return null
  if (parsed.metric.panel === 'supplyDemand') {
    const summary = await fetchSupplyDemandSummary()
    const regions = Array.isArray(summary.regions) ? summary.regions : (await fetchSupplyDemandRegions()).regions || []
    return {
      type: 'supplyDemand',
      source: summary.source || summary.data_source || 'api',
      snapshot: {
        station: parsed.station,
        metric: parsed.metric,
        current: summary.total_deficit_10k_m3,
        status: summary.warning_region_count > 0 ? 'warning' : 'normal',
        time: summary.timestamp,
        summary,
        regions,
      },
      reply: formatSupplyDemandReply(summary, regions),
    }
  }
  const snapshot = await fetchStationSnapshot(parsed.station.station_id)
  return {
    type: 'monitoring',
    source: snapshot.source || snapshot.data_source || 'api',
    snapshot: {
      station: parsed.station,
      metric: parsed.metric,
      current: valueForMetric(snapshot, parsed.metric.key),
      status: snapshot.status || 'normal',
      time: snapshot.timestamp,
      values: snapshot,
    },
    reply: formatStationReply(snapshot, parsed.metric),
  }
}

function valueForMetric(snapshot, metricKey) {
  const value = snapshot[metricKey]
  if (metricKey === 'water_quality' && value) {
    return `grade ${value.grade || '-'}, turbidity ${value.turbidity_ntu || '-'} NTU`
  }
  if (metricKey === 'device_status' && value) {
    return `online ${value.online || 0}, warning ${value.warning || 0}, offline ${value.offline || 0}`
  }
  return value == null ? '-' : value
}

function formatStationReply(snapshot, metric) {
  const mainValue = valueForMetric(snapshot, metric.key)
  // 精简播报：只报核心数据，去掉冗长的同步指标列表和来源/时间标注
  const inflow = snapshot.inflow_m3s ?? '-'
  const outflow = snapshot.outflow_m3s ?? '-'
  const level = snapshot.water_level_m ?? '-'
  return `${snapshot.name || snapshot.station_id}${metric.label}当前值为 ${mainValue}${metric.unit ? ` ${metric.unit}` : ''}。同步指标：入库流量 ${inflow} m3/s，出库流量 ${outflow} m3/s，库水位 ${level} m。`
}

function formatSupplyDemandReply(summary, regions) {
  const sourceLabel = (summary.source || summary.data_source) === 'frontend_fallback' ? '前端兜底数据' : '统一供需 API'
  const worst = regions.slice().sort((a, b) => Number(b.deficit_10k_m3 || 0) - Number(a.deficit_10k_m3 || 0))[0]
  const worstText = worst ? `，最大缺口区域为 ${worst.name}，缺口 ${worst.deficit_10k_m3 || 0} 万m3` : ''
  return `供需平衡汇总：总需水 ${summary.total_demand_10k_m3} 万m3，实际供水 ${summary.total_actual_supply_10k_m3} 万m3，总缺口 ${summary.total_deficit_10k_m3} 万m3，预警区域 ${summary.warning_region_count} 个${worstText}。来源：${sourceLabel}。`
}
